import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg, fmtMinutes } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Play } from "lucide-react";

const COLUMNS = [
  { id: "todo", label: "To Do", dot: "bg-slate-400" },
  { id: "in_progress", label: "In Progress", dot: "bg-amber-400" },
  { id: "done", label: "Done", dot: "bg-emerald-400" },
];

const PRIORITY_COLORS = {
  urgent: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-blue-500",
  low: "border-l-slate-600",
};

export default function Kanban() {
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = async () => {
    try {
      const [t, a] = await Promise.all([api.get("/tasks-with-time"), api.get("/agents")]);
      setTasks(t.data);
      setAgents(a.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); }, []);

  const move = async (taskId, status) => {
    const prev = tasks;
    setTasks(tasks.map((t) => (t.id === taskId ? { ...t, status } : t)));
    try {
      await api.patch(`/tasks/${taskId}`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
    } catch (e) {
      setTasks(prev);
      toast.error(errMsg(e));
    }
  };

  const startTimer = async (taskId) => {
    try {
      await api.post("/timer/start", { task_id: taskId });
      toast.success("Timer started — see Timekeeper");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const agentName = (aid) => agents.find((a) => a.id === aid)?.name || "Unassigned";

  return (
    <div className="space-y-6" data-testid="kanban-board">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Task Board</h1>
        <p className="text-sm text-muted-foreground mt-1">Drag cards between lanes. Every task links back to its ticket.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 items-start">
        {COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col.id);
          return (
            <div
              key={col.id}
              data-testid={`kanban-column-${col.id}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
              onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
              onDrop={(e) => { e.preventDefault(); setOverCol(null); if (dragId) move(dragId, col.id); setDragId(null); }}
              className={`rounded-lg border border-border bg-card/50 p-3 min-h-[300px] transition-colors ${overCol === col.id ? "kanban-col-drop" : ""}`}
            >
              <div className="flex items-center gap-2 px-1 pb-3">
                <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                <span className="font-mono-digits text-xs uppercase tracking-widest text-muted-foreground">{col.label}</span>
                <span className="ml-auto font-mono-digits text-xs text-muted-foreground">{colTasks.length}</span>
              </div>
              <div className="space-y-2.5">
                {colTasks.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`kanban-task-card-${t.id}`}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    className={`rounded-md border border-border border-l-4 ${PRIORITY_COLORS[t.priority] || "border-l-slate-600"} bg-card p-3.5 cursor-grab active:cursor-grabbing hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-500/5 transition-all ${dragId === t.id ? "opacity-40" : ""}`}
                  >
                    <p className="text-sm font-semibold leading-snug">{t.title}</p>
                    <Link to={`/tickets/${t.ticket_id}`} className="inline-block mt-1.5 font-mono-digits text-[10px] tracking-widest text-orange-400 hover:underline">
                      {t.ticket_number}
                    </Link>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground truncate">{agentName(t.assignee_id)}</span>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 font-mono-digits text-[11px] text-muted-foreground">
                          <Clock className="h-3 w-3" /> {fmtMinutes(t.logged_minutes || 0)}
                        </span>
                        <button data-testid={`kanban-timer-${t.id}`} onClick={() => startTimer(t.id)}
                          className="p-1.5 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors" title="Start timer">
                          <Play className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-8 border border-dashed border-border rounded-md">Drop tasks here</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
